"use client";

import React from "react";
import styles from "./OnboardingProcess.module.css";
import { aiCallAgentOnboardingSteps as steps } from "../../data/ai-call-agent";
import { useServiceData } from "../../hooks/useServiceData";

function Icon({ name, color = "#fff" }) {
  switch (name) {
    case "search":
      return (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
          <path
            d="M11 19a8 8 0 100-16 8 8 0 000 16z"
            stroke={color}
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M21 21l-4.35-4.35"
            stroke={color}
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    case "brain":
      return (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
          <path
            d="M12 3v18"
            stroke={color}
            strokeWidth="1.5"
            strokeLinecap="round"
          />
          <path
            d="M6 7a4 4 0 018 0v0a4 4 0 010 8v0a4 4 0 01-8 0v0"
            stroke={color}
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M18 7a4 4 0 00-8 0v0a4 4 0 008 0v0"
            stroke={color}
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    case "rocket":
      return (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
          <path
            d="M22 2s-4 1-6 3c-1.2 1.2-2 3-2 3s-1.8.8-3 2c-2 2-3 6-3 6s1.2 0 3-1c2-1 3-2 3-2s1.8-.8 3-2c2-2 3-6 3-6z"
            stroke={color}
            strokeWidth="1"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    case "chart":
      return (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
          <path
            d="M3 3v18h18"
            stroke={color}
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M9 13v6M15 9v10M19 5v14"
            stroke={color}
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    default:
      return null;
  }
}

export default function OnboardingProcess({ dynamicData }) {
  const { data: backendPageData } = useServiceData();

  // Requirement: Integrate dynamic data into the existing section
  const sourceData = dynamicData || backendPageData?.extraSections?.find(s => s.type === "processSection")?.refId;

  const sectionTitle = sourceData?.title || "Our Simple 4-Step Process";
  const sectionDescription = sourceData?.description || "Your Journey to AI Automation in 4 Simple Steps";
  const finalSteps = (sourceData?.steps && sourceData.steps.length > 0) ? sourceData.steps : steps;
  return (
    <section className={styles.wrapper} aria-labelledby="onboarding-heading">
      {/* CTA banner above onboarding */}
      {/* <div
        className={styles.ctaBanner}
        role="region"
        aria-label="Try AI Voice Demo"
      >
        <div className={styles.ctaInner}>
          <div className={styles.ctaText}>
            Experience our AI Voice in action — try the live demo now.
          </div>
          <div>
            <a
              className={styles.ctaButton}
              href="https://www.codeqlik.com/ray/ai_voice.html"
              target="_blank"
              rel="noopener noreferrer"
            >
              Try Demo
            </a>
          </div>
        </div>
      </div> */}
      <div className={styles.container}>
        <div className={styles.heading} data-reveal="fade">
          <h3
            id="onboarding-heading"
            className="fw-bold"
            style={{ color: "#fff" }}
          >
            {sectionTitle}
          </h3>
          <p className={styles.intro}>
            {sectionDescription}
          </p>
          <p className={styles.intro} style={{ marginTop: 8 }}>
            We’ve engineered our onboarding process to be as seamless and
            intelligent as our AI. From initial strategy to full-scale
            deployment, we guide you every step of the way to ensure a
            successful launch.
          </p>
        </div>

        {/* Desktop: Alternating central timeline */}
        <div className={styles.timelineDesktop} aria-hidden={false}>
          <div className={styles.centralLine} aria-hidden />
          {finalSteps.map((s, idx) => {
            const side = idx % 2 === 0 ? styles.left : styles.right;
            const stepId = s.id || (idx + 1);
            const stepColor = s.color || "#F48428";
            return (
              <article
                key={stepId}
                className={`${styles.timelineStep} ${side}`}
                aria-labelledby={`step-${stepId}-title`}
              >
                <div className={styles.stepInner} tabIndex={0}>
                  <div className={styles.iconRow}>
                    <div
                      className={styles.iconCircle}
                      style={{ borderColor: stepColor }}
                      aria-hidden
                    >
                      <Icon name={s.icon} color={stepColor} />
                    </div>
                    <div>
                      <div id={`step-${stepId}-title`} className={styles.title}>
                        {s.title}
                      </div>
                      <div className={styles.stepNumber}>Step {stepId}</div>
                    </div>
                  </div>

                  {s.description ? (
                    <div className="mt-3">
                      <p className={styles.sub} style={{ color: "rgba(255,255,255,0.7)", fontSize: "0.95rem", lineHeight: "1.6" }}>
                        {s.description}
                      </p>
                    </div>
                  ) : (
                    <>
                      <div className={styles.activities}>
                        <strong>Activities:</strong>
                        <ul>
                          {s.activities?.map((a) => (
                            <li key={a}>{a}</li>
                          ))}
                        </ul>
                      </div>

                      <div className={styles.outcome}>
                        <strong>Outcome:</strong> {s.outcome}
                      </div>
                    </>
                  )}
                </div>
                <div className={styles.connector} aria-hidden />
              </article>
            );
          })}
        </div>

        {/* Mobile: Accessible accordion */}
        <div className={styles.accordionMobile}>
          {finalSteps.map((s, idx) => {
            const stepId = s.id || (idx + 1);
            const stepColor = s.color || "#F48428";
            return (
              <details key={stepId} className={styles.accordionItem}>
                <summary
                  className={styles.accordionSummary}
                  aria-controls={`panel-${stepId}`}
                  id={`summary-${stepId}`}
                >
                  <div
                    className={styles.iconCircle}
                    style={{ borderColor: stepColor }}
                    aria-hidden
                  >
                    <Icon name={s.icon} color={stepColor} />
                  </div>
                  <div style={{ marginLeft: 12 }}>
                    <div className={styles.title}>{s.title}</div>
                    <div className={styles.stepNumber}>Step {stepId}</div>
                  </div>
                </summary>
                <div
                  id={`panel-${stepId}`}
                  className={styles.accordionPanel}
                  role="region"
                  aria-labelledby={`summary-${stepId}`}
                >
                  {s.description ? (
                    <div className="p-3 pt-0">
                      <p className={styles.sub} style={{ color: "rgba(255,255,255,0.7)", fontSize: "0.9rem" }}>
                        {s.description}
                      </p>
                    </div>
                  ) : (
                    <>
                      <div className={styles.activities}>
                        <strong>Activities:</strong>
                        <ul>
                          {s.activities?.map((a) => (
                            <li key={a}>{a}</li>
                          ))}
                        </ul>
                      </div>
                      <div className={styles.outcome}>
                        <strong>Outcome:</strong> {s.outcome}
                      </div>
                    </>
                  )}
                </div>
              </details>
            );
          })}
        </div>
      </div>
    </section>
  );
}
